<template>
    <div class="w-full hover:bg-white/10 rounded-xl" @click="selectImage">
        <div class="relative bg-white/10 rounded-xl border border-white/20">
            <div class="w-full pl-4 pr-4 h-5 text-center bg-white/15 text-ui-active border-b border-white/20 rounded-t-xl text-xs align-middle leading-4">
                {{ modelValue.labelName }}
            </div>
            <img :src="modelValue.src" :alt="modelValue.filename" class="w-full max-h-48 object-contain cursor-pointer p-2 pt-1"
                draggable="true" @dragstart="onDragStart" />
            <!-- Hamburger menu button -->
            <div v-if="editable" class="absolute top-0 right-0 mr-1">
                <button @click.stop="toggleMenu"
                    class="hover:bg-white/15 text-slate-400 hover:text-ui-active rounded-full focus:outline-none" title="Menu">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 20 20"
                        stroke="currentColor" stroke-width="2">
                        <circle cx="10" cy="5" r="1" />
                        <circle cx="10" cy="10" r="1" />
                        <circle cx="10" cy="15" r="1" />
                    </svg>
                </button>
                <div v-if="showMenu" class="absolute right-0 bg-ui-bg border border-white/20 rounded shadow-lg z-30 text-slate-300 text-sm">
                    <button class="block w-full text-left px-4 py-1 hover:bg-white/10 hover:text-ui-active"
                        @click.stop="onOpenEditor">Edit</button>
                    <button class="block w-full text-left px-4 py-1 hover:bg-white/10 hover:text-ui-active"
                        @click.stop="onCopyImage">Copy</button>
                    <button class="block w-full text-left px-4 py-1 hover:bg-white/10 hover:text-ui-active"
                        @click.stop="onRenamingFromMenu">Rename</button>
                    <button class="block w-full text-left px-4 py-1 hover:bg-white/10 hover:text-ui-active"
                        @click.stop="onDownloadImage">Download</button>
                    <hr class="border-white/20" />
                    <button class="block w-full font-bold text-left px-4 py-1 hover:bg-red-900/20 text-red-400"
                        @click.stop="deleteImage">Delete</button>
                </div>
            </div>
            <!-- Delete button -->
            <button v-if="editable"
                class="absolute top-0 left-0 ml-1 hover:bg-slate-600 text-slate-500 hover:text-red-400 rounded-full"
                @click.stop="deleteImage" title="Delete image">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 20 20"
                    stroke="currentColor" stroke-width="2">
                    <line x1="6" y1="6" x2="14" y2="14" stroke="currentColor" stroke-linecap="round" />
                    <line x1="14" y1="6" x2="6" y2="14" stroke="currentColor" stroke-linecap="round" />
                </svg>
            </button>
            <div class="text-center truncate text-slate-200 rounded-b-xl bg-white/15 border-t border-white/20 pl-1 pr-1">
                <div v-if="!renaming" @dblclick="startRenaming" class="cursor-pointer">{{ modelValue.filename }}</div>
                <input v-else v-model="newFilename" @keyup.enter="saveRename" @blur="saveRename"
                    class="text-white bg-white/15 rounded px-1 w-full border border-white/20" ref="renameInput" />
                <div class="text-xs text-slate-400">{{ modelValue.imageSize }}</div>
                <!-- <div class="text-xs">{{  modelValue.id }} - {{ modelValue.labelId }}</div> -->
            </div>
            <FloatingPopup v-if="showEditor" :model-value="showEditor" :opacity=100
                @update:modelValue="showEditor = $event" :position="{ x: 200, y: 200 }" :width="700" :height="600"
                title="Image Editor">
                <ImageEditor :src="modelValue.src" @update="onImageEdit" @save="onImageSave" />
            </FloatingPopup>
        </div>
    </div>
</template>


<script setup>
import { ref, watch, nextTick } from 'vue';
import { useImageStore } from '../../stores/imageStore.js';
import FloatingPopup from '../FloatingPopup.vue';
import ImageEditor from './ImageEditor.vue';

const imageStore = useImageStore();

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    editable: {
        type: Boolean,
        required: false,
        default: true,
    }
});

const emit = defineEmits(['update:modelValue', 'select', 'delete', 'image-copied']);

const showEditor = ref(false);
const showMenu = ref(false);
const renaming = ref(false);
const newFilename = ref(props.modelValue.filename);
const renameInput = ref(null);


function toggleMenu() {
    showMenu.value = !showMenu.value;
}

function onOpenEditor() {
    showEditor.value = true;
    showMenu.value = false;
}

function onDownloadImage() {
    showMenu.value = false;
    const link = document.createElement('a');
    link.href = props.modelValue.src;
    link.download = props.modelValue.filename || 'image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

async function onCopyImage() {
    showMenu.value = false;
    // Create a new image in the DB with id as prefix on the filename
    await imageStore.duplicateImage(props.modelValue.id);
    emit('image-copied');
}

// Drag and drop support
function onDragStart(e) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('image-id', props.modelValue.id);
}

// Hide menu on click outside
if (typeof window !== 'undefined') {
    window.addEventListener('click', (e) => {
        showMenu.value = false;
    });
}

const selectImage = () => {
    emit('select', props.modelValue);
};

const deleteImage = () => {
    emit('delete', props.modelValue.id);
};

const onImageEdit = (newSrc) => {
    // Update the image src in the DB and emit
    emit('update:modelValue', { ...props.modelValue, src: newSrc });
};

const onImageSave = async (newSrc, width, height) => {
    // Update the image src in the DB
    const updated = { ...props.modelValue, src: newSrc, width, height };
    await imageStore.updateImage(updated);
    emit('update:modelValue', updated);
    showEditor.value = false;
};

function startRenaming() {
    renaming.value = true;
    newFilename.value = props.modelValue.filename;
    nextTick(() => {
        if (renameInput.value) renameInput.value.focus();
    });
}

function onRenamingFromMenu() {
    showMenu.value = false;
    startRenaming();
}

async function saveRename() {
    if (!renaming.value) return;
    renaming.value = false;
    const trimmed = newFilename.value.trim();
    if (trimmed && trimmed !== props.modelValue.filename) {
        const updated = { ...props.modelValue, filename: trimmed };
        await imageStore.updateImage(updated);
        emit('update:modelValue', updated);
    }
}
</script>
